import { motion, useReducedMotion, useScroll, useTransform } from 'motion/react'
import { useRef } from 'react'
import { site } from '../content/site'
import { ArrowIcon } from './ArrowIcon'

export function Hero() {
  const ref = useRef<HTMLElement>(null)
  const reduceMotion = useReducedMotion()
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] })
  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', reduceMotion ? '0%' : '5%'])
  const copyY = useTransform(scrollYProgress, [0, 1], ['0%', reduceMotion ? '0%' : '-3%'])

  return (
    <section className="hero" id="top" ref={ref}>
      <div className="shell hero-grid">
        <motion.div
          className="hero-copy"
          style={{ y: copyY }}
          initial={reduceMotion ? false : 'hidden'}
          animate="show"
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.065, delayChildren: 0.08 } },
          }}
        >
          <motion.div
            className="eyebrow"
            variants={{
              hidden: { opacity: 0, y: 10 },
              show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
            }}
          >
            {site.kicker}
          </motion.div>

          <div className="hero-title-wrap" aria-label={site.brand}>
            {site.brand.split('').map((letter, index) => (
              <span className="hero-letter-mask" key={`${letter}-${index}`}>
                <motion.span
                  className="hero-letter"
                  aria-hidden="true"
                  variants={{
                    hidden: { y: '112%', rotate: 2 },
                    show: { y: 0, rotate: 0, transition: { duration: 0.82, ease: [0.16, 1, 0.3, 1] } },
                  }}
                >
                  {letter}
                </motion.span>
              </span>
            ))}
          </div>

          <motion.div
            className="hero-brand-rule"
            aria-hidden="true"
            variants={{
              hidden: { scaleX: 0 },
              show: { scaleX: 1, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
            }}
          />

          <motion.h1
            variants={{
              hidden: { opacity: 0, y: 16 },
              show: { opacity: 1, y: 0, transition: { duration: 0.68, ease: [0.22, 1, 0.36, 1] } },
            }}
          >
            {site.headline}
          </motion.h1>
          <motion.p
            className="hero-subhead"
            variants={{
              hidden: { opacity: 0, y: 12 },
              show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
            }}
          >
            {site.subhead}
          </motion.p>
          <motion.div
            className="hero-actions"
            variants={{
              hidden: { opacity: 0, y: 10 },
              show: { opacity: 1, y: 0, transition: { duration: 0.52 } },
            }}
          >
            <a className="button button--primary" href="#intent">
              Read letter of intent <ArrowIcon />
            </a>
            <a className="text-link" href="#store">
              View store concept <ArrowIcon />
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          className="hero-visual media-cut"
          initial={reduceMotion ? false : { clipPath: 'polygon(0 0, 0 0, 0 100%, 0 100%)' }}
          animate={{ clipPath: 'polygon(0 0, 98% 0, 100% 3%, 100% 100%, 2% 100%, 0 97%)' }}
          transition={{ duration: 1.05, delay: 0.18, ease: [0.16, 1, 0.3, 1] }}
        >
          <motion.img
            style={{ y: imageY }}
            initial={reduceMotion ? false : { scale: 1.075, filter: 'grayscale(1) contrast(0.9)' }}
            animate={{ scale: 1, filter: 'grayscale(0) contrast(1)' }}
            transition={{ duration: 1.35, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
            src="/images/bisen-kiosk-reference.jpg"
            alt="Reference kiosk showing the proposed green cellphone-accessories retail direction inside a mall"
            fetchPriority="high"
          />
          {!reduceMotion && (
            <motion.div
              className="hero-reveal-panel"
              aria-hidden="true"
              initial={{ x: '0%' }}
              animate={{ x: '105%' }}
              transition={{ duration: 0.95, delay: 0.14, ease: [0.76, 0, 0.24, 1] }}
            />
          )}
          <div className="media-stamp" aria-hidden="true">
            <span>B/</span>
            <span>Retail proposal</span>
          </div>
          <div className="hero-visual-caption">
            <span>Proposed kiosk</span>
            <span>Visible · organized · approachable</span>
          </div>
        </motion.div>
      </div>
      <div className="shell hero-foot">
        <span>{site.proposalLine}</span>
        <a href="#intent" className="scroll-cue">Continue ↓</a>
      </div>
    </section>
  )
}
